import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Box,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import DataCard, { tableHeaderCellSx } from "./components/DataCard";
import apiClient from "./utils/apiClient";
import Footer from "./Footer";

const Usage = () => {
  const [usageRecords, setUsageRecords] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);

  useEffect(() => {
    const fetchUsage = async () => {
      const response = await apiClient.post("/run-request", { action: "usage" });
      const records = Array.isArray(response)
        ? response
        : Array.isArray(response?.records)
          ? response.records
          : [];
      setUsageRecords(records);
    };
    fetchUsage();
  }, []);

  // **Process Data for Rollup**
  const aggregatedData = {};
  const detailedData = {};
  usageRecords.forEach((record) => {
    const user = record.user_id || "unknown";
    const product = record.product || "unknown";
    const started = Number(record.total_count) || 0;
    const completed = Number(record.completed_count) || 0;
    const error = Number(record.error_count) || 0;

    if (!aggregatedData[user]) {
      aggregatedData[user] = {
        started: 0,
        completed: 0,
        error: 0,
        clones_processed: 0,
      };
      detailedData[user] = {};
    }

    if (!detailedData[user][product]) {
      detailedData[user][product] = {
        started: 0,
        completed: 0,
        error: 0,
        clones_processed: 0,
      };
    }

    aggregatedData[user].started += started;
    aggregatedData[user].completed += completed;
    aggregatedData[user].error += error;
    detailedData[user][product].started += started;
    detailedData[user][product].completed += completed;
    detailedData[user][product].error += error;
  });
  return (
    <PageShell>
      <PageContainer maxWidth={1080}>
        <PageHeader
          icon={<ShowChartIcon />}
          title="Usage"
          subtitle="Per-user runs broken down by product"
        />
        <DataCard noPadding>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeaderCellSx} />
                    <TableCell sx={tableHeaderCellSx}>User</TableCell>
                    <TableCell sx={tableHeaderCellSx} align="right">Started</TableCell>
                    <TableCell sx={tableHeaderCellSx} align="right">Completed</TableCell>
                    <TableCell sx={tableHeaderCellSx} align="right">Errors</TableCell>
                    <TableCell sx={tableHeaderCellSx} align="right">Clones Processed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(aggregatedData).map(([user, totals]) => (
                    <React.Fragment key={user}>
                      {/* **Rollup Row** */}
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setExpandedUser(
                                expandedUser === user ? null : user
                              )
                            }
                          >
                            {expandedUser === user ? (
                              <KeyboardArrowUp />
                            ) : (
                              <KeyboardArrowDown />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>{user}</TableCell>
                        <TableCell align="right">{totals.started}</TableCell>
                        <TableCell align="right">{totals.completed}</TableCell>
                        <TableCell align="right">{totals.error}</TableCell>
                        <TableCell align="right">
                          {totals.clones_processed}
                        </TableCell>
                      </TableRow>

                      {/* **Expandable Detailed Row** */}
                      <TableRow>
                        <TableCell colSpan={6} sx={{ padding: 0 }}>
                          <Collapse
                            in={expandedUser === user}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box
                              sx={{
                                margin: 2,
                                backgroundColor: "#f9f9f9",
                                padding: 2,
                              }}
                            >
                              <Typography variant="subtitle1">
                                Product Details for {user}
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>
                                      <strong>Product</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                      <strong>Started</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                      <strong>Completed</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                      <strong>Errors</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                      <strong>Clones Processed</strong>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {Object.entries(detailedData[user]).map(
                                    ([product, counts]) => (
                                      <TableRow key={product}>
                                        <TableCell>{product}</TableCell>
                                        <TableCell align="right">
                                          {counts.started}
                                        </TableCell>
                                        <TableCell align="right">
                                          {counts.completed}
                                        </TableCell>
                                        <TableCell align="right">
                                          {counts.error}
                                        </TableCell>
                                        <TableCell align="right">
                                          {counts.clones_processed}
                                        </TableCell>
                                      </TableRow>
                                    )
                                  )}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
        </DataCard>
      </PageContainer>
    </PageShell>
  );
};

export default Usage;
